import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { DateService } from '@lemon/core/service/date.service'

import { LocalDate, LocalMonth } from '@lemon/core/model/common'

import { Location } from '@angular/common'
import { KirjanpidonProjekti } from '@lemon/core-firebase/model/kirjanpito-projektit'
import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { KirjanpitoService } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { TilikarttaService } from 'app/_angular/service/tilikartta.service'
import { Tilikausi } from 'app/_jaettu-lemonator/model/asiakas'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { map, takeWhile } from 'rxjs/operators'
import { LaskunToimittajaTypeahead } from '../_angular/service/lasku/lasku.typeahead'
import { LaskunToimittajaTypeaheadBase } from '../_jaettu-angular/service/lasku/typeahead.service'
import { KirjanpitoRaporttiValilehti } from '../_jaettu-lemonator/model/kirjanpitaja'
import { KlikattuKirjaus } from './raportit.component'

export interface BroadcastChannelKlikattuRaportointikirjausPayload {
  maksutavanTunniste: string
  kirjauksenAvain: string
  kirjauksenKuukausi: LocalMonth
}

/**
 * Component that is served from asiakkaat/:asiakasAvain/kirjanpidon-raportit/:vuosi/:kuukausi/:tyyppi
 */
@Component({
  templateUrl: './raportit-standalone.component.html',
  styleUrls: ['./raportit-standalone.component.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [{ provide: LaskunToimittajaTypeaheadBase, useClass: LaskunToimittajaTypeahead }],
})
export class KirjanpitoRaportitStandaloneComponent implements OnInit, OnDestroy {
  valittuKuukausiObservable: BehaviorSubject<LocalMonth> = new BehaviorSubject({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 })
  asiakkaanTiedotObservable: Observable<string>
  valittuValilehti: KirjanpitoRaporttiValilehti = 'tase-ja-tulos'
  private _ngUnsubscribe = new Subject<void>()
  private _kirjausSelectedBroadcastChannel: BroadcastChannel

  kirjanpidonProjektitObservable: Observable<KirjanpidonProjekti[]> = this._kirjanpitoService.kirjanpidonProjektitObservable

  constructor(
    private _route: ActivatedRoute,
    private _dateService: DateService,
    private _asiakasService: AsiakasService,
    private _location: Location,
    private _kirjanpitoService: KirjanpitoService,
    private _tilikarttaService: TilikarttaService,
  ) {}

  ngOnInit() {
    this.asiakkaanTiedotObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        if (asiakas) {
          return asiakas.nimi + ' (' + asiakas.ytunnus + ')'
        }
        return ''
      }),
    )

    const paramMap = this._route.snapshot.paramMap
    const asiakasAvain = paramMap.get('asiakasAvain')
    const vuosi = paramMap.get('vuosi')
    const kuukausi = paramMap.get('kuukausi')

    const typeSuspect = paramMap.get('tyyppi') as KirjanpitoRaporttiValilehti
    if (typeSuspect === 'paakirja' || typeSuspect === 'tase-ja-tulos' || typeSuspect === 'viralliset' || typeSuspect === 'bruttolaskelma' || typeSuspect === 'oss-myynnit') {
      this.valittuValilehti = typeSuspect
    } else if (typeSuspect) {
      throw new Error('Unknown report type "' + typeSuspect + '"')
    }

    this._asiakasService.nykyisenAsiakkaanTilikaudetObservable.pipe(takeWhile(tilikaudet => !tilikaudet?.length, true)).subscribe(tilikaudet => {
      const kuukausiLocalMonth: LocalMonth = { year: Number(vuosi), month: Number(kuukausi) }
      this.valittuKuukausiObservable.next(kuukausiLocalMonth)
      const tilikausi = this._findTilikausiForLocalMonth(tilikaudet, kuukausiLocalMonth)
      this._tilikarttaService.asetaAktiivinenTilikausiKonteksti(tilikausi, kuukausiLocalMonth)
    })

    try {
      this._kirjausSelectedBroadcastChannel = new BroadcastChannel(asiakasAvain + '-kirjausnumero-selected')
    } catch (err) {
      console.error('Failed to initialize BroadcastChannel', err)
    }
  }

  ngOnDestroy() {
    if (this._kirjausSelectedBroadcastChannel) {
      this._kirjausSelectedBroadcastChannel.close()
      this._kirjausSelectedBroadcastChannel = null
    }
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  raporttikirjaustaKlikatiin(klikattuKirjaus: KlikattuKirjaus) {
    if (!klikattuKirjaus?.kirjausnumero || !this._kirjausSelectedBroadcastChannel) {
      return
    }

    this._kirjausSelectedBroadcastChannel.postMessage(klikattuKirjaus)
  }

  selectedReportTypeChanged(type: KirjanpitoRaporttiValilehti) {
    const paramMap = this._route.snapshot.paramMap
    const asiakasAvain = paramMap.get('asiakasAvain')
    const vuosi = paramMap.get('vuosi')
    const kuukausi = paramMap.get('kuukausi')
    this._location.replaceState('/asiakkaat/' + asiakasAvain + '/kirjanpidon-raportit/' + vuosi + '/' + kuukausi + '/' + type)
  }

  private _findTilikausiForLocalMonth(tilikaudet: Tilikausi[], localMonth: LocalMonth): Tilikausi {
    if (!tilikaudet?.length) {
      return null
    }
    const asLocalDate: LocalDate = { year: localMonth.year, month: localMonth.month, day: 15 }
    for (const tilikausi of tilikaudet) {
      if (this._dateService.onkoLocalDateKahdenValissa(asLocalDate, tilikausi.alkaa, tilikausi.loppuu)) {
        return tilikausi
      }
      if (tilikausi.alkaa.year === localMonth.year && tilikausi.alkaa.month === localMonth.month) {
        return tilikausi
      }
      if (tilikausi.loppuu.year === localMonth.year && tilikausi.loppuu.month === localMonth.month) {
        return tilikausi
      }
    }
    return null
  }
}
